import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import FAQSection, { higherOrLowerFaqs } from '../components/FAQSection';
import { flagSrcSet, flagUrl } from '../data/countries';
import {
  buildComparisonPool,
  formatEntryValue,
  getEntryKey,
  getEntryValue,
  getMetricLabel,
  getMetricPromptWord,
  pickComparisonPair,
  pickNextComparison,
} from '../data/higherOrLowerEntities';
import { getHigherOrLowerRank, pickWrongAnswerRoast } from '../utils/higherOrLowerRoasts';
import { getItem, setItem } from '../utils/safeStorage';
import '../styles/HigherOrLower.css';

const HIGHSCORE_KEY = 'hol-population-highscore';
const REVEAL_MS = 1400;

const randomPrompt = () => (Math.random() < 0.5 ? 'higher' : 'lower');

function EntryVisual({ entry }) {
  return (
    <div className="hol-flag">
      <img
        src={flagUrl(entry.code)}
        srcSet={flagSrcSet(entry.code)}
        alt={`${entry.countryName || entry.name} flag`}
        width="80"
        height="80"
      />
    </div>
  );
}

function RevealedValue({ value, active, formatValue }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!active) {
      setDisplay(0);
      return undefined;
    }
    const start = performance.now();
    const duration = 700;
    let frame;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(value * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, active]);

  return <p className="hol-value">{formatValue(display)}</p>;
}

export default function HigherOrLowerPage() {
  const pool = useMemo(() => buildComparisonPool(), []);
  const poolRef = useRef(pool);

  const [{ left, right }, setPair] = useState(() => pickComparisonPair(pool));
  const [prompt, setPrompt] = useState(() => randomPrompt());
  const [score, setScore] = useState(0);
  const [highscore, setHighscore] = useState(0);
  const [phase, setPhase] = useState('playing');
  const [wasCorrect, setWasCorrect] = useState(null);
  const [pickedSide, setPickedSide] = useState(null);
  const [wrongLine, setWrongLine] = useState(null);
  const [resultRank, setResultRank] = useState(null);
  const timeoutRef = useRef(null);
  const pairRef = useRef({ left, right });
  const scoreRef = useRef(0);
  const promptRef = useRef(prompt);

  poolRef.current = pool;
  pairRef.current = { left, right };
  scoreRef.current = score;
  promptRef.current = prompt;

  useEffect(() => {
    const stored = Number(getItem(HIGHSCORE_KEY));
    if (Number.isFinite(stored) && stored > 0) setHighscore(stored);
  }, []);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const startGame = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const nextPair = pickComparisonPair(poolRef.current);
    setPair(nextPair);
    setPrompt(randomPrompt());
    setScore(0);
    setPhase('playing');
    setWasCorrect(null);
    setPickedSide(null);
    setWrongLine(null);
    setResultRank(null);
  }, []);

  const handlePick = useCallback((side) => {
    if (phase !== 'playing') return;

    const current = pairRef.current;
    const mode = promptRef.current;
    const leftValue = getEntryValue(current.left);
    const rightValue = getEntryValue(current.right);
    const pickedValue = side === 'left' ? leftValue : rightValue;
    const otherValue = side === 'left' ? rightValue : leftValue;
    const correct = mode === 'higher' ? pickedValue > otherValue : pickedValue < otherValue;

    setPickedSide(side);
    setWasCorrect(correct);
    setPhase('revealing');

    if (correct) {
      const nextScore = scoreRef.current + 1;
      setScore(nextScore);
      setWrongLine(null);
      setHighscore((best) => {
        if (nextScore <= best) return best;
        setItem(HIGHSCORE_KEY, String(nextScore));
        return nextScore;
      });
    } else {
      setWrongLine(pickWrongAnswerRoast());
      setResultRank(getHigherOrLowerRank(scoreRef.current));
    }

    timeoutRef.current = setTimeout(() => {
      if (correct) {
        const staying = side === 'left' ? current.left : current.right;
        const leaving = side === 'left' ? current.right : current.left;
        const nextRight = pickNextComparison(
          poolRef.current,
          staying,
          [getEntryKey(leaving)]
        );
        setPair({ left: staying, right: nextRight });
        setPrompt(randomPrompt());
        setWasCorrect(null);
        setPickedSide(null);
        setPhase('playing');
      } else {
        setPhase('gameover');
      }
    }, REVEAL_MS);
  }, [phase]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePick('left');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handlePick('right');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePick]);

  const revealing = phase === 'revealing' || phase === 'gameover';
  const playing = phase === 'playing';
  const resultClass = wasCorrect == null ? '' : wasCorrect ? 'is-correct' : 'is-wrong';
  const seo = getPageSeo('higher-or-lower');
  const structuredData = seo?.structuredData
    ? [seo.structuredData, buildFAQSchema(higherOrLowerFaqs)]
    : buildFAQSchema(higherOrLowerFaqs);
  const promptWord = prompt === 'higher' ? 'higher' : 'lower';
  const sameMetric = left.metric === right.metric;
  const sameAnimalKind =
    left.metric === 'animals' &&
    right.metric === 'animals' &&
    left.animal === right.animal;
  const promptMetric = sameMetric
    ? (left.metric === 'animals' && !sameAnimalKind ? 'number' : getMetricPromptWord(left))
    : 'number';

  const sideProps = (side) => {
    const entry = side === 'left' ? left : right;
    const metric = getMetricLabel(entry);
    return {
      role: 'button',
      tabIndex: playing ? 0 : -1,
      onClick: () => handlePick(side),
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handlePick(side);
        }
      },
      className: [
        'hol-side',
        `hol-side-${side}`,
        playing ? 'is-pickable' : '',
        pickedSide === side ? resultClass : '',
      ].filter(Boolean).join(' '),
      'aria-label': `Pick ${entry.name} ${metric} as the ${promptWord} ${sameMetric ? metric.toLowerCase() : 'number'}`,
    };
  };

  const renderEntry = (entry, side) => {
    const formatValue = (value) => formatEntryValue(entry, value);
    const metric = getMetricLabel(entry);

    return (
      <>
        <EntryVisual entry={entry} />
        <h2 className="hol-country">{entry.name}</h2>
        <p className="hol-metric">{metric}</p>
        {side === 'right' && !revealing ? (
          <p className="hol-value hol-value-unknown">?</p>
        ) : side === 'right' && revealing ? (
          <div className="hol-reveal">
            <RevealedValue
              key={getEntryKey(entry)}
              value={getEntryValue(entry)}
              active={revealing}
              formatValue={formatValue}
            />
            <p className={`hol-result-label ${resultClass}`}>
              {wasCorrect ? 'Correct!' : (wrongLine || 'Wrong!')}
            </p>
          </div>
        ) : (
          <p className="hol-value">{formatEntryValue(entry, getEntryValue(entry))}</p>
        )}
      </>
    );
  };

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={structuredData}
      />

      <section className="hol-game" aria-label="Higher or Lower game">
        <h1 className="hol-visually-hidden">Higher or Lower: Compare Numbers</h1>

        <header className="hol-header">
          <div className="hol-scores">
            <div className="hol-score-block hol-score-high">
              <span className="hol-score-number">{highscore}</span>
              <span className="hol-score-label">Highscore</span>
            </div>
            <div className="hol-score-block hol-score-current">
              <span className="hol-score-number">{score}</span>
              <span className="hol-score-label">Score</span>
            </div>
          </div>
        </header>

        <div className="hol-board">
          <article {...sideProps('left')}>
            {renderEntry(left, 'left')}
          </article>

          <div className="hol-divider">
            <div className="hol-prompt" aria-live="polite">
              <span className="hol-prompt-line">Which {promptMetric}</span>
              <span className="hol-prompt-line hol-prompt-emphasis">is {promptWord}?</span>
            </div>
          </div>

          <article {...sideProps('right')}>
            {renderEntry(right, 'right')}
          </article>
        </div>

        {phase === 'gameover' && (
          <div className="hol-overlay" role="dialog" aria-labelledby="hol-gameover-title">
            <div className="hol-overlay-card">
              <h2 id="hol-gameover-title">Game Over</h2>
              <p className="hol-overlay-score">Score: {score}</p>
              <p className="hol-overlay-best">Best: {highscore}</p>
              {resultRank && (
                <>
                  <div className="hol-rank-row">
                    <span className="hol-rank-label">Your Rank:</span>
                    <span className="hol-rank-animal">{resultRank.animal}</span>
                  </div>
                  <p className="hol-overlay-roast">{resultRank.message}</p>
                </>
              )}
              <button type="button" className="hol-btn hol-btn-replay" onClick={startGame}>
                Play Again
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="hol-extras">
        <h2>How to Play Higher or Lower</h2>
        <p>
          Each card shows a country with a number: population, territory size
          (km²), or an animal count such as pigeons in Italy or cattle in India.
          The center asks which number is higher or lower. You compare the
          numbers as they are — even when one side is people, another is km²,
          and another is animals. If Poland’s territory size is 312,696 and the
          other card is pigeons in Italy at about 4,800,000, pigeons are higher
          because 4,800,000 is the bigger number.
        </p>
        <ul>
          <li>Same type: “Which population is higher?” — pick the country with more people.</li>
          <li>Mixed types: pick whichever number is higher or lower, even if one is people, one is km², and one is an animal count.</li>
          <li>Click a side, or use the left and right arrow keys.</li>
          <li>Your best score is saved in this browser.</li>
        </ul>
      </section>

      <FAQSection mode="higherOrLower" />
    </>
  );
}
